import { useEffect, useState } from 'react'
import { getCoverageEvent } from './api'
import type { CoverageEventResponse, CoverageFeedResponse } from '@/shared/api/types'

/**
 * The cameras on a set of events, which the list endpoint does not send.
 *
 * `CoverageEventService.list()` passes `List.of()` for feeds — deliberately, to
 * avoid an N+1 across up to 200 rows — so `event.feeds` is empty on every list
 * response and only `GET /coverage/events/{id}` fills it. Any screen that has to
 * offer a *camera* rather than an *event* therefore needs a second read.
 *
 * ## Why a client-side fan-out is acceptable here and nowhere else
 *
 * It is bounded twice: the callers filter to events that are still open, and
 * `cap` bounds whatever survives that. Both pickers show a handful of events at
 * a time. This is not a pattern to reach for on the diary, which is paginated to
 * 200 and would issue 200 requests — that screen keeps its camera counts server-
 * side, and gets them properly when the backend batches feeds into the list.
 *
 * `truncated` is returned rather than swallowed: a picker silently missing the
 * eleventh event's cameras is the same defect as a map silently missing markers.
 */
export function useEventFeeds(events: CoverageEventResponse[], cap = 12) {
  const [feeds, setFeeds] = useState<Map<string, CoverageFeedResponse[]>>(new Map())
  const [loading, setLoading] = useState(false)

  // Keyed on the id list, not the array identity: `useLoad` and `useMemo` hand
  // back a fresh array on every render, and re-fetching on identity would make
  // this loop forever.
  const key = events.slice(0, cap).map((e) => e.id).join(',')

  useEffect(() => {
    if (!key) { setFeeds(new Map()); return }
    let cancelled = false
    setLoading(true)
    const ids = key.split(',')
    Promise.all(ids.map((id) =>
      getCoverageEvent(id)
        .then((e) => [id, e.feeds] as const)
        // One event failing must not take the whole picker down; that event
        // simply offers no existing camera, and "+ New camera" still works.
        .catch(() => [id, [] as CoverageFeedResponse[]] as const)))
      .then((pairs) => { if (!cancelled) setFeeds(new Map(pairs)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [key])

  return { feeds, loading, truncated: events.length > cap, cap }
}
